import { Component, OnDestroy, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { BanksService } from '../../../services/banks.service';
import { environment } from '../../../../environments/environment';
import { ThirdPartiesService } from '../../../services/third-parties.service';
import { ProjectService } from '../../../services/project.service';
import { ReportJobsService } from '../../../services/report-jobs.service';
import { ReportHubService } from '../../../services/report-hub.service';
import { ToastService } from '../../../services/toast.service';

@Component({
  selector: 'app-expense-receipts-reports',
  templateUrl: './expense-receipts-reports.component.html',
  styleUrls: ['./expense-receipts-reports.component.scss']
})
export class ExpenseReceiptsReportsComponent implements OnInit, OnDestroy {
  startDate: Date = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  endDate: Date = new Date();
  selectedBankId: number | null = null;
  selectedThirdPartyId: number | null = null;
  selectedProjectId: number | null = null;
  loading = false;

  banks: any[] = [];
  thirdParties: any[] = [];
  projects: any[] = [];

  // Dropdown options for PrimeNG
  banksDropdown: any[] = [];
  thirdPartiesDropdown: any[] = [];
  projectsDropdown: any[] = [];

  private reportHubSub!: Subscription;
  private pendingJobId: string | null = null;

  constructor(
    private http: HttpClient,
    private banksService: BanksService,
    private thirdPartiesService: ThirdPartiesService,
    private projectService: ProjectService,
    private reportJobsService: ReportJobsService,
    private reportHubService: ReportHubService,
    private toastService: ToastService
  ) { }

  ngOnInit(): void {
    this.loadBanks();
    this.loadThirdParties();
    this.loadProjects();

    this.reportHubSub = this.reportHubService.reportStatusChanged$.subscribe(notification => {
      if (notification.jobId !== this.pendingJobId) return;

      if (notification.status === 'Completed') {
        this.loading = false;
        this.toastService.showToast('success', 'Reporte de egresos generado exitosamente. Descargando...');
        this.reportJobsService.downloadReport(notification.jobId, notification.fileName || undefined);
        this.pendingJobId = null;
      } else if (notification.status === 'Failed') {
        this.loading = false;
        this.toastService.showToast('error', `Error al generar el reporte: ${notification.errorMessage || 'Desconocido'}`);
        this.pendingJobId = null;
      } else if (notification.status === 'Processing') {
        this.toastService.showToast('info', 'Procesando reporte de egresos...');
      }
    });
  }

  ngOnDestroy(): void {
    this.reportHubSub?.unsubscribe();
  }

  // Helper para extraer datos de estructura paginada o array directo
  private extractData(response: any): any[] {
    if (Array.isArray(response)) {
      return response;
    }
    if (response && response.data) {
      // Manejar estructura paginada: data puede ser array directo o {data: [...], totalCount: ...}
      return Array.isArray(response.data) ? response.data : (response.data?.data || []);
    }
    return [];
  }

  loadBanks(): void {
    this.banksService.getAll().subscribe({
      next: (response: any) => {
        this.banks = this.extractData(response);
        this.banksDropdown = [
          { label: 'Todos los bancos', value: null },
          ...this.banks.map(bank => ({
            label: bank.bankName,
            value: bank.id
          }))
        ];
      },
      error: (error) => {
        console.error('Error loading banks:', error);
        this.banksDropdown = [{ label: 'Todos los bancos', value: null }];
      }
    });
  }

  loadThirdParties(): void {
    this.thirdPartiesService.getAll().subscribe({
      next: (response: any) => {
        this.thirdParties = this.extractData(response);
        this.thirdPartiesDropdown = [
          { label: 'Todos los terceros', value: null },
          ...this.thirdParties.map(tp => ({
            label: `${tp.firstName} ${tp.lastName} - ${tp.typeDocument || 'N/A'}: ${tp.documentId || 'N/A'}`,
            value: tp.id
          }))
        ];
      },
      error: (error) => {
        console.error('Error loading third parties:', error);
        this.thirdPartiesDropdown = [{ label: 'Todos los terceros', value: null }];
      }
    });
  }

  loadProjects(): void {
    this.projectService.getAll().subscribe({
      next: (response: any) => {
        this.projects = this.extractData(response);
        this.projectsDropdown = [
          { label: 'Todos los proyectos', value: null },
          ...this.projects.map(project => ({
            label: `${project.projectId} - ${project.name}`,
            value: project.id
          }))
        ];
      },
      error: (error) => {
        console.error('Error loading projects:', error);
        this.projectsDropdown = [{ label: 'Todos los proyectos', value: null }];
      }
    });
  }

  // 🔍 Búsqueda server-side para terceros
  onSearchThirdParties(event: any): void {
    const query = event.filter;
    if (!query || query.length < 3) {
      this.loadThirdParties();
      return;
    }
    this.thirdPartiesService.getAllFiltered(query).subscribe({
      next: (response: any) => {
        this.thirdParties = this.extractData(response);
        this.thirdPartiesDropdown = [
          { label: 'Todos los terceros', value: null },
          ...this.thirdParties.map(tp => ({
            label: `${tp.firstName} ${tp.lastName} - ${tp.typeDocument || 'N/A'}: ${tp.documentId || 'N/A'}`,
            value: tp.id
          }))
        ];
      },
      error: (err) => console.error('Error searching third parties:', err)
    });
  }

  // 🔍 Búsqueda server-side para proyectos
  onSearchProjects(event: any): void {
    const query = event.filter;
    if (!query || query.length < 3) {
      this.loadProjects();
      return;
    }
    this.projectService.getAllFiltered(query).subscribe({
      next: (response: any) => {
        this.projects = this.extractData(response);
        this.projectsDropdown = [
          { label: 'Todos los proyectos', value: null },
          ...this.projects.map(project => ({
            label: `${project.projectId} - ${project.name}`,
            value: project.id
          }))
        ];
      },
      error: (err) => console.error('Error searching projects:', err)
    });
  }

  // Date Type Filter
  dateType: string = 'Creation';
  dateTypeOptions: any[] = [
    { label: 'Fecha de Creación', value: 'Creation' },
    { label: 'Fecha de Pago', value: 'Payment' }
  ];

  // Sort options
  sortBy: string = 'Id';
  sortByOptions: any[] = [
    { label: 'ID Comprobante', value: 'Id' },
    { label: 'Fecha de Pago', value: 'PaymentDate' },
    { label: 'Fecha de Creación', value: 'CreationDate' }
  ];

  sortDirection: string = 'asc';
  sortDirectionOptions: any[] = [
    { label: 'Ascendente', value: 'asc' },
    { label: 'Descendente', value: 'desc' }
  ];

  downloadExcelReport(): void {
    if (!this.startDate || !this.endDate) {
      this.toastService.showToast('warning', 'Por favor seleccione un rango de fechas');
      return;
    }

    this.loading = true;
    this.reportJobsService.queueExpenseReceiptReport({
      startDate: this.startDate,
      endDate: this.endDate,
      bankId: this.selectedBankId,
      thirdPartyId: this.selectedThirdPartyId,
      projectId: this.selectedProjectId,
      dateType: this.dateType,
      sortBy: this.sortBy,
      sortDirection: this.sortDirection
    }).subscribe({
      next: (response) => {
        this.pendingJobId = response.jobId;
        this.toastService.showToast('info', 'Generando reporte de egresos en segundo plano...');
      },
      error: (error) => {
        console.error('Error requesting report:', error);
        this.loading = false;
        this.toastService.showToast('error', 'Error al solicitar el reporte');
      }
    });
  }

  get selectedBankName(): string {
    if (!this.selectedBankId) return 'Todos los bancos';
    const bank = this.banks.find(b => b.id === this.selectedBankId);
    return bank ? bank.bankName : 'Todos';
  }

  get selectedThirdPartyName(): string {
    if (!this.selectedThirdPartyId) return 'Todos los terceros';
    const tp = this.thirdParties.find(t => t.id === this.selectedThirdPartyId);
    return tp ? `${tp.firstName} ${tp.lastName} - ${tp.typeDocument || 'N/A'}: ${tp.documentId || 'N/A'}` : 'Todos';
  }

  get selectedProjectName(): string {
    if (!this.selectedProjectId) return 'Todos los proyectos';
    const project = this.projects.find(p => p.id === this.selectedProjectId);
    return project ? `${project.projectId} - ${project.name}` : 'Todos';
  }
}
